import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from './BookingForm.jsx';
import { PhoneIcon, WhatsAppIcon } from './ContactIcons.jsx';
import { blogPosts, clinic, doctors, featuredTreatments, reviews } from '../data/site.js';

const commonNeeds = [
  { label: 'Dental Implants', href: '/treatments/implants/' },
  { label: 'Full-Mouth Rehabilitation', href: '/treatments/implants/#services' },
  { label: 'Aesthetic Dentistry', href: '/treatments/cosmetic/' },
  { label: 'Root Canal Treatment', href: '/treatments/restorative/' },
  { label: 'Aligners / Dental Braces', href: '/treatments/cosmetic/#services' },
  { label: 'Crowns & Bridges (Caps)', href: '/treatments/restorative/#services' },
  { label: 'Tooth Filling', href: '/treatments/restorative/#services' },
  { label: 'Tooth Extraction', href: '/treatments/restorative/#services' },
  { label: 'Tooth pain / emergency dentist', href: '/treatments/emergency/' },
  { label: 'Kids dentist', href: '/treatments/children/' }
];

const homeFaqs = [
  [
    'Who should I visit for tooth pain in Varanasi?',
    'If you have tooth pain, swelling, sensitivity, a broken tooth or pain while chewing, book a dental examination. The dentist can check whether the cause is decay, infection, gum disease, a cracked tooth or another issue.'
  ],
  [
    'Do I need a dental check-up if nothing hurts?',
    'Yes. Many early dental problems do not cause pain at first. Regular checks can help identify plaque buildup, cavities, gum inflammation and old filling problems earlier.'
  ],
  [
    'Can I book for root canal treatment directly?',
    'You can book for a root canal consultation, but the dentist must examine the tooth first. Some painful teeth need root canal treatment, while others may need a filling, crown, gum treatment, extraction or monitoring.'
  ],
  [
    'Is dental implant treatment suitable for everyone?',
    'No. Dental implants depend on bone support, gum health, medical history, oral hygiene and the condition of nearby teeth. A consultation is needed before deciding whether implants are suitable.'
  ],
  [
    'How can I contact My Dentist quickly?',
    'You can call, WhatsApp, use the online booking form or open the clinic location on Google Maps. For urgent pain or swelling, call or WhatsApp the clinic for faster guidance.'
  ]
];

const REVIEWS_PER_PAGE = 6;
const PHILOSOPHY_SLIDE_INTERVAL_MS = 3000;

const philosophySlides = [
  {
    src: '/images/clinic-exterior-real.png',
    alt: 'My Dentist clinic exterior and entrance in Varanasi'
  },
  {
    src: '/images/clinic-slideshow-reception-desk.jpeg',
    alt: 'Reception desk and waiting area at My Dentist dental clinic'
  },
  {
    src: '/images/clinic-slideshow-waiting-area.jpeg',
    alt: 'Comfortable patient waiting area inside My Dentist clinic'
  },
  {
    src: '/images/clinic-slideshow-treatment-room.jpeg',
    alt: 'Dental treatment room at My Dentist clinic in Varanasi'
  },
  {
    src: '/images/clinic-slideshow-consult-room.jpeg',
    alt: 'Consultation room at My Dentist dental clinic'
  }
];

export default function HomePage() {
  const featuredPosts = blogPosts.slice(0, 3);
  const [activeDoctorIndex, setActiveDoctorIndex] = useState(0);
  const [activePhilosophySlide, setActivePhilosophySlide] = useState(0);
  const [isPhilosophyPaused, setIsPhilosophyPaused] = useState(false);
  const [reviewPage, setReviewPage] = useState(0);
  const activeDoctor = doctors[activeDoctorIndex];
  const reviewPageCount = Math.ceil(reviews.length / REVIEWS_PER_PAGE);
  const visibleReviews = reviews.slice(reviewPage * REVIEWS_PER_PAGE, (reviewPage + 1) * REVIEWS_PER_PAGE);

  useEffect(() => {
    if (isPhilosophyPaused || philosophySlides.length < 2) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const intervalId = window.setInterval(() => {
      setActivePhilosophySlide((slide) => (slide + 1) % philosophySlides.length);
    }, PHILOSOPHY_SLIDE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isPhilosophyPaused]);

  function showPreviousReviews() {
    setReviewPage((page) => (page === 0 ? reviewPageCount - 1 : page - 1));
  }

  function showNextReviews() {
    setReviewPage((page) => (page + 1) % reviewPageCount);
  }

  return (
    <>
      <section className="hero" id="hero">
        <div className="container">
          <div className="hero__inner">
            <div className="hero__content">
              <h1 className="heading-display hero__headline">Your family's home for healthy smiles.</h1>
              <p className="text-body hero__text">
                General, cosmetic, restorative and emergency dental care in Varanasi, explained clearly before treatment begins.
              </p>
              <div className="hero__actions">
                <Link to="/#booking" className="btn btn--primary">Book an Appointment</Link>
                <a href={clinic.phoneHref} className="btn btn--secondary btn--with-icon hero__call-button" aria-label={`Call ${clinic.phoneDisplay}`}>
                  <PhoneIcon />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="priority-treatments" id="treatments" aria-labelledby="priority-treatments-title">
        <div className="container">
          <div className="priority-treatments__header">
            <h2 className="heading-2" id="priority-treatments-title">Dental Treatments</h2>
            <p className="text-body">Common care options at My Dentist.</p>
          </div>
          <div className="priority-treatments__grid">
            {featuredTreatments.map((treatment) => (
              <Link className="priority-card" to={treatment.href} key={treatment.title}>
                <div className="priority-card__image">
                  <img src={treatment.image} alt={treatment.imageAlt} loading="lazy" />
                </div>
                <div className="priority-card__body">
                  <span className="priority-card__eyebrow">{treatment.eyebrow}</span>
                  <h3>{treatment.title}</h3>
                  <p>{treatment.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="philosophy" id="philosophy">
        <div className="container">
          <blockquote className="heading-2 philosophy__statement">Clear answers before treatment begins.</blockquote>
          <p className="text-body philosophy__body">
            Patients often visit a dentist when they are already worried: a tooth hurts, a filling has broken, a child is uncomfortable, or they are unsure whether treatment will be complicated or costly. Our aim is to make the appointment easier to understand from the beginning.
          </p>
          <figure
            className="philosophy__image philosophy-slideshow"
            aria-label="My Dentist clinic photo slideshow"
            onMouseEnter={() => setIsPhilosophyPaused(true)}
            onMouseLeave={() => setIsPhilosophyPaused(false)}
            onFocus={() => setIsPhilosophyPaused(true)}
            onBlur={() => setIsPhilosophyPaused(false)}
          >
            <div className="philosophy-slideshow__viewport">
              {philosophySlides.map((slide, index) => (
                <img
                  className={`philosophy-slideshow__slide${activePhilosophySlide === index ? ' is-active' : ''}`}
                  src={slide.src}
                  alt={slide.alt}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  aria-hidden={activePhilosophySlide === index ? 'false' : 'true'}
                  key={slide.src}
                />
              ))}
            </div>
            <div className="philosophy-slideshow__controls" aria-label="Choose clinic photo">
              {philosophySlides.map((slide, index) => (
                <button
                  className={`philosophy-slideshow__dot${activePhilosophySlide === index ? ' is-active' : ''}`}
                  type="button"
                  aria-label={`Show clinic photo ${index + 1}`}
                  aria-current={activePhilosophySlide === index ? 'true' : 'false'}
                  onClick={() => setActivePhilosophySlide(index)}
                  key={slide.src}
                />
              ))}
            </div>
          </figure>
        </div>
      </section>

      <section className="dentist" id="dentist">
        <div className="container">
          <div className="dentist__inner">
            <div className="dentist__intro">
              <p className="caption dentist__overline">Clinical team</p>
              <h2 className="heading-1 dentist__name">Meet your dentists</h2>
            </div>
            <div className="dentist-tabs" role="tablist" aria-label="Clinic dentists">
              {doctors.map((doctor, index) => (
                <button
                  type="button"
                  className={`dentist-tabs__button${activeDoctorIndex === index ? ' is-active' : ''}`}
                  role="tab"
                  aria-selected={activeDoctorIndex === index}
                  aria-controls="dentist-profile"
                  key={doctor.name}
                  onClick={() => setActiveDoctorIndex(index)}
                >
                  {doctor.name}
                </button>
              ))}
            </div>

            <article className="dentist-profile" id="dentist-profile">
              <div className="dentist-profile__media">
                <img src={activeDoctor.image} alt={activeDoctor.imageAlt} loading="lazy" />
              </div>
              <div className="dentist-profile__content">
                <p className="dentist-profile__role">{activeDoctor.role}</p>
                <h3 className="dentist-profile__name">{activeDoctor.name}</h3>
                <p className="dentist-profile__experience">{activeDoctor.experience}</p>
                <p className="dentist-profile__qualification">{activeDoctor.qualification}</p>
                <p className="dentist-profile__summary">{activeDoctor.summary}</p>
                <ul className="dentist-profile__focus">
                  {activeDoctor.focus.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link to={`/dentists/${activeDoctor.slug}/`} className="btn btn--secondary dentist-profile__button">Read more</Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="needs" id="needs">
        <div className="container">
          <h2 className="heading-2 needs__heading">What can we help you with?</h2>
          <div className="needs__list">
            {commonNeeds.map((need) => (
              <Link key={need.label} to={need.href} className="needs__item">
                <span className="needs__item-text">{need.label}</span>
                <span className="needs__item-arrow" aria-hidden="true">+</span>
              </Link>
            ))}
          </div>
          <p className="text-body needs__note">
            If you are not sure which treatment page fits your concern, start with an appointment request and the clinic will guide you to the right visit type.
          </p>
        </div>
      </section>

      <section className="booking" id="booking">
        <div className="container">
          <div className="booking__header">
            <h2 className="heading-2 booking__heading">Book an appointment</h2>
            <p className="text-body booking__subtext">
              Choose a reason for your visit and select a time that works for you. The clinic team will confirm availability within one working day.
            </p>
          </div>
          <BookingForm />
        </div>
      </section>

      <section className="reviews" id="reviews">
        <div className="container">
          <div className="reviews__header">
            <h2 className="heading-2">Reviews</h2>
            <p className="reviews__rating">Rated 5 stars on Google</p>
            <p className="text-body reviews__intro">Hear from patients sharing their experience with My Dentist Dental Clinic.</p>
          </div>

          <div className="reviews__carousel" aria-label="Google review cards">
            <button className="reviews__nav reviews__nav--prev" type="button" onClick={showPreviousReviews} aria-label="Show previous reviews">
              <span aria-hidden="true">&lt;</span>
            </button>
            <div className="reviews__list">
              {visibleReviews.map((review) => {
              const reviewStars = String.fromCharCode(9733).repeat(review.rating);

              return (
                <article className="review" key={review.author}>
                  <div className="review__profile">
                    <div className="review__meta">
                      <span className="review__google" aria-label="Google review">G</span>
                      <span className="review__stars" aria-label={`${review.rating} star review`}>{reviewStars}</span>
                    </div>
                    <blockquote className="review__quote">{review.quote}</blockquote>
                  </div>
                  <div className="review__footer">
                    <div className="review__avatar" aria-hidden="true">{review.author.charAt(0)}</div>
                    <div>
                      <p className="review__author">{review.author}</p>
                      <p className="review__details">{review.timeAgo}</p>
                    </div>
                  </div>
                </article>
              );
            })}
            </div>
            <button className="reviews__nav reviews__nav--next" type="button" onClick={showNextReviews} aria-label="Show more reviews">
              <span aria-hidden="true">&gt;</span>
            </button>
          </div>
          <div className="reviews__footer">
            <a href={clinic.gmbUrl} target="_blank" rel="noreferrer" className="btn btn--text">View all Google reviews</a>
          </div>
        </div>
      </section>

      <section className="home-faq" id="home-faq">
        <div className="container lp-faq__layout">
          <div className="lp-faq__heading">
            <p className="caption">Before you book</p>
            <h2 className="heading-2">Common questions from Varanasi patients.</h2>
            <p className="text-body">General guidance only. The safest next step depends on a dental examination, your symptoms and your health history.</p>
          </div>
          <div className="lp-faq__list">
            {homeFaqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="blog-preview" id="blog">
        <div className="container">
          <div className="blog-preview__header">
            <div>
              <p className="caption">Dental knowledge, without the jargon</p>
              <h2 className="heading-2">From the blog</h2>
            </div>
            <p className="text-body blog-preview__intro">Practical answers for Varanasi patients about prevention, treatment choices and what to expect before you book.</p>
            <Link to="/blog/" className="btn btn--text">Browse every article</Link>
          </div>

          <div className="blog-preview__grid">
            <article className="blog-preview__featured">
              <div>
                <span className="caption blog-preview__category">{featuredPosts[0].category}</span>
                <h3 className="blog-preview__featured-title">
                  <Link to={`/blog/${featuredPosts[0].slug}/`}>{featuredPosts[0].title}</Link>
                </h3>
                <p>{featuredPosts[0].summary}</p>
              </div>
              <div className="blog-preview__footer">
                <span>{featuredPosts[0].readTime}</span>
                <Link to={`/blog/${featuredPosts[0].slug}/`}>Read article</Link>
              </div>
            </article>

            <div className="blog-preview__secondary">
              {featuredPosts.slice(1).map((post) => (
                <article className="blog-preview__article" key={post.slug}>
                  <span className="caption">{post.category}</span>
                  <h3><Link to={`/blog/${post.slug}/`}>{post.title}</Link></h3>
                  <p>{post.summary}</p>
                  <Link to={`/blog/${post.slug}/`} className="blog-preview__read">{post.readTime}</Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="location" id="location">
        <div className="container">
          <h2 className="heading-2 location__heading">Find us</h2>
          <div className="location__inner">
            <div className="location__details">
              <div className="location__group">
                <p className="location__group-heading">Address</p>
                <p>{clinic.addressLines.map((line) => <span key={line}>{line}<br /></span>)}</p>
              </div>
              <div className="location__group">
                <p className="location__group-heading">Opening hours</p>
                <p>{clinic.openingHours.map((line) => <span key={line}>{line}<br /></span>)}</p>
              </div>
              <div className="location__group">
                <p className="location__group-heading">Access</p>
                <p>My Dentist serves patients from Varanasi and nearby areas including Lanka, Bhelupur, BHU, Sigra, Assi and Mahmoorganj. Open the Google Maps listing for directions, or contact the clinic before your visit for parking, access and landmark details.</p>
              </div>
              <div className="location__actions">
                <a href={clinic.phoneHref} className="location__quick-link" aria-label={`Call ${clinic.phoneDisplay}`}>
                  <PhoneIcon />
                  Call Now
                </a>
                <a href={clinic.whatsappHref} className="location__quick-link location__quick-link--with-icon" target="_blank" rel="noreferrer">
                  <WhatsAppIcon />
                  WhatsApp
                </a>
                <a href={clinic.mapUrl} className="location__quick-link" target="_blank" rel="noreferrer">Directions</a>
                <a href={clinic.gmbUrl} className="location__quick-link" target="_blank" rel="noreferrer">Google profile</a>
                <Link to="/#booking" className="location__quick-link">Book online</Link>
              </div>
            </div>
            <div className="location__map map-card">
              <iframe
                title={`${clinic.listingName} map`}
                src={clinic.mapEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
